import useLangText from '@shared/hooks/useLangText';
import {
    ProcessFieldBase,
    type ProcessFieldDateRange as ProcessFieldDateRangeType,
} from '@shared/types/process';

import {
    isAfterCurrentPeriod,
    isBeforeCurrentPeriod,
    isSameOrAfterCurrentPeriod,
    isSameOrBeforeCurrentPeriod,
} from '../ProcessFieldDate/dateValidation';
import { ProcessFieldDateRangePickerAiChat } from './ProcessFieldDateRangePickerAiChat';

import { ShadcnForm } from '@shadcn-features/core';
import { ShadcnFormDateRangePickerFlowProps } from '@shadcn-features/core/ShadcnForm/fields';
import { ShadcnFormRule } from '@shadcn-features/core/ShadcnForm/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends ShadcnFormDateRangePickerFlowProps {
    field: ProcessFieldBase & ProcessFieldDateRangeType;
    variant?: 'default' | 'ai-chat';
}

export function ProcessFieldDateRangeField({
    field,
    fieldProps,
    variant,
    ...props
}: Props) {
    const { t } = useTranslation();
    const dateType = field.options.dateType;

    const picker = useMemo(() => {
        if (dateType === 'datetime') {
            return 'date';
        }

        return dateType ?? undefined;
    }, [dateType]);

    const getLangText = useLangText();

    const rules: Record<
        Exclude<ProcessFieldDateRangeType['options']['validation'], null>,
        ShadcnFormRule<Date[] | undefined>[0]['validator']
    > = {
        any: () => {
            return true;
        },
        future: (value?: Date[]) => {
            if (!value || !value[0] || !value[1]) return true;

            if (
                !isAfterCurrentPeriod(value[0], dateType) ||
                !isAfterCurrentPeriod(value[1], dateType)
            ) {
                return t('dateMustBeFuture');
            }

            return true;
        },

        futureOrToday: (value?: Date[]) => {
            if (!value || !value[0] || !value[1]) return true;

            if (
                !isSameOrAfterCurrentPeriod(value[0], dateType) ||
                !isSameOrAfterCurrentPeriod(value[1], dateType)
            ) {
                return t('dateMustBeTodayOrFuture');
            }

            return true;
        },

        past: (value?: Date[]) => {
            if (!value || !value[0] || !value[1]) return true;

            if (
                !isBeforeCurrentPeriod(value[0], dateType) ||
                !isBeforeCurrentPeriod(value[1], dateType)
            ) {
                return t('dateMustBePast');
            }

            return true;
        },

        pastOrToday: (value?: Date[]) => {
            if (!value || !value[0] || !value[1]) return true;

            if (
                !isSameOrBeforeCurrentPeriod(value[0], dateType) ||
                !isSameOrBeforeCurrentPeriod(value[1], dateType)
            ) {
                return t('dateMustBeTodayOrPast');
            }

            return true;
        },
    };

    const DateRangePicker =
        variant === 'ai-chat'
            ? ProcessFieldDateRangePickerAiChat
            : ShadcnForm.DateRangePickerFlow;
    return (
        <DateRangePicker
            fieldProps={fieldProps}
            format={field.options.format || undefined}
            tooltip={getLangText(field?.tooltip ?? '')}
            {...props}
            picker={picker}
            rules={[
                { validator: rules[field.options.validation || 'any'] },
                ...(props.rules || []),
            ]}
        />
    );
}
